import { Observable } from 'rxjs';
import { PaginatedDataService } from '../../../services/paginated-data.service';
import { DataSourceAdapter } from './data-source-adapter.interface';

/**
 * Union type for all supported data source inputs.
 * The component auto-detects the type and creates the appropriate adapter.
 *
 * ## Supported Types
 *
 * **Static Array** - For small, fixed datasets:
 * ```ts
 * [dataSource]="myItems"
 * ```
 *
 * **Observable** - For reactive data that changes over time:
 * ```ts
 * [dataSource]="items$"
 * ```
 *
 * **PaginatedDataService** - For server-side pagination (recommended for large datasets):
 * ```ts
 * // In component:
 * dataService = inject(PaginatedDataService<MyRow, string>);
 *
 * // In template:
 * [dataSource]="dataService"
 * ```
 *
 * **Custom Adapter** - For special cases, implement DataSourceAdapter<T>
 *
 * @template T - Row data type
 *
 * @see resolveDataSource - Auto-detects and wraps the source
 * @see PaginatedDataService - Handles server-side pagination
 */
export type DataTableSource<T> =
  | T[]
  | Observable<T[]>
  | PaginatedDataService<T, any>
  | DataSourceAdapter<T>;

/**
 * Type for row selection identifiers.
 * Must match the type of your `rowIdField` values.
 */
export type SelectionKey = string | number;
